/**
 * Service to handle all chat-related API calls
 */

const API_URL = process.env.REACT_APP_API_URL;

/**
 * Send a message to the AI and get a response.
 * Handles sending to an existing conversation or starting a new one.
 * @param {Array} messages - Array of message objects with role and content (including the latest user message)
 * @param {String} systemPrompt - System prompt to guide AI behavior
 * @param {String|null} conversationId - ID of the existing conversation, or null to start a new one
 * @param {String} token - Firebase Auth ID token
 * @param {String|null} characterId - ID of the character (optional)
 * @param {String|null} characterName - Name of the character (optional, for Bela)
 * @returns {Promise<object>} - Object containing AI response data and conversationId
 */
export const sendChatMessage = async (messages, systemPrompt, conversationId, token, characterId = null, characterName = null) => {
  const payload = {
    messages,
    systemPrompt,
    conversationId, // Include conversationId (can be null)
    characterId,
    characterName
  };

  const response = await fetch(`${API_URL}/ai/chat`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`, // Add Authorization header
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    // Try to parse error response for more details
    let errorBody;
    try {
      errorBody = await response.json();
    } catch (e) {
      errorBody = await response.text();
    }
    console.error("Chat API Error:", response.status, errorBody);
    const errorMessage = (typeof errorBody === 'object' && errorBody?.error)
      ? errorBody.error
      : (typeof errorBody === 'string' ? errorBody : response.statusText);
    throw new Error(errorMessage);
  }

  const data = await response.json();

  console.log("Chat API Response:", data); // Keep this log for debugging

  // Expecting { success: true, data: { aiResponse: {...}, conversationId: "..." } }
  if (!data.success || !data.data || !data.data.aiResponse || !data.data.conversationId) {
    console.error("Invalid chat response format:", data);
    throw new Error('Invalid response format from chat endpoint');
  }

  // Return data.data which contains { aiResponse, conversationId }
  return data.data; 
};

/**
 * Fetch a list of conversations for the authenticated user.
 * @param {String} token - Firebase Auth ID token
 * @param {number} limit - Number of conversations to fetch
 * @param {string|null} startAfter - Cursor for pagination (optional)
 * @returns {Promise<object>} - Object containing conversations array and nextPageCursor
 */
export const getConversations = async (token, limit = 20, startAfter = null) => {
  // Only fetch active conversations — history is loaded separately via getCharacterConversations
  const payload = { limit, status: 'active' };
  if (startAfter) {
    payload.startAfter = startAfter;
  }

  const response = await fetch(`${API_URL}/ai/conversations`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    throw new Error('Failed to fetch conversations');
  }

  const data = await response.json();

  if (!data.success || !data.data || !data.data.conversations) {
    throw new Error('Invalid response format for conversations');
  }

  return data.data; // { conversations: [], nextPageCursor: "..." | null }
};

/**
 * Fetch all messages for a specific conversation.
 * @param {String} token - Firebase Auth ID token
 * @param {String} conversationId - The ID of the conversation
 * @returns {Promise<object>} - Object containing messages array and systemPrompt
 */
export const getConversationMessages = async (token, conversationId, limit = 40, before = null) => {
  const payload = { conversationId, limit };
  if (before) payload.before = before;

  const response = await fetch(`${API_URL}/ai/conversations/messages`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch messages for conversation ${conversationId}`);
  }

  const data = await response.json();

  if (!data.success || !data.data || !data.data.messages) {
    throw new Error('Invalid response format for conversation messages');
  }

  return data.data; // { messages: [], systemPrompt: "..." | null }
};

/**
 * Delete a specific conversation.
 * @param {String} token - Firebase Auth ID token
 * @param {String} conversationId - The ID of the conversation to delete
 * @returns {Promise<object>} - Success/error message from the backend
 */
export const deleteConversation = async (token, conversationId) => {
  const payload = { conversationId };

  const response = await fetch(`${API_URL}/ai/conversations/delete`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });

  const data = await response.json(); // Attempt to parse JSON regardless of status

  if (!response.ok) {
    console.error("Delete Conversation API Error:", response.status, data);
    throw new Error(data.message || `Failed to delete conversation: ${response.statusText}`);
  }

  if (!data.success) {
    console.error("Delete Conversation Failed:", data);
    throw new Error(data.message || 'Failed to delete conversation');
  }

  return data; // { success: true, message: "..." }
};

/**
 * Archive a conversation (move to history). Backend enforces 10-conv cap.
 * @param {String} token
 * @param {String} conversationId
 */
export const archiveConversation = async (token, conversationId) => {
  const response = await fetch(`${API_URL}/ai/conversations/archive`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
    body: JSON.stringify({ conversationId }),
  });
  const data = await response.json();
  if (!response.ok || !data.success) throw new Error(data.error || 'Failed to archive conversation');
  return data; // { success: true, archivedConversationId }
};

/**
 * Resume an archived conversation (sets status back to 'active').
 * @param {String} token
 * @param {String} conversationId
 */
export const resumeConversation = async (token, conversationId) => {
  const response = await fetch(`${API_URL}/ai/conversations/resume`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
    body: JSON.stringify({ conversationId }),
  });
  const data = await response.json();
  if (!response.ok || !data.success) throw new Error(data.error || 'Failed to resume conversation');
  return data; // { success: true, resumedConversationId }
};

/**
 * Fetch archived conversations for a specific character.
 * @param {String} token
 * @param {String} characterId
 * @param {String} status - 'archived' | 'active' (default 'archived')
 * @param {String|null} startAfter - cursor for pagination
 * @param {Number} limit - max number of conversations to fetch
 */
export const getCharacterConversations = async (token, characterId, status = 'archived', startAfter = null, limit = 10) => {
  const response = await fetch(`${API_URL}/ai/conversations`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
    body: JSON.stringify({ characterId, status, limit, startAfter }),
  });
  const data = await response.json();
  if (!response.ok || !data.success) throw new Error(data.error || 'Failed to fetch character conversations');
  return data.data; // { conversations: [], nextPageCursor }
};